import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../../hooks/useTheme';
import { ARCHIMATE_LAYERS, getArchimateColors, getElementsByLayer } from '../../types/archimateElements';
import { getArchimateIconPath } from '../../utils/archimateIcons';
import { useNavigate } from 'react-router-dom';

// Material UI icons
import MouseIcon from '@mui/icons-material/Mouse';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import SaveIcon from '@mui/icons-material/Save';
import DescriptionIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const ToolbarButton = ({ title, onClick, active, children, style = {} }) => {
  const { theme } = useTheme();
  return (
    <button
      title={title}
      onClick={onClick}
      style={{
        background: active ? theme.toolbar.buttonBackgroundActive : theme.toolbar.buttonBackground,
        color: theme.toolbar.buttonText,
        border: `1px solid ${theme.toolbar.buttonBorder}`,
        borderRadius: 6,
        padding: '6px 8px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        fontSize: '14px',
        minWidth: '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        ...style
      }}
      onMouseEnter={(e) => { if (!active) e.target.style.backgroundColor = theme.name === 'light' ? '#f1f5f9' : '#374151'; }}
      onMouseLeave={(e) => { if (!active) e.target.style.backgroundColor = theme.toolbar.buttonBackground; }}
    >
      {children}
    </button>
  );
};

// Названия слоёв на русском
const LAYER_LABELS = {
  strategy: 'Стратегия',
  business: 'Бизнес',
  application: 'Приложения',
  technology: 'Технологии',
  physical: 'Физический',
  implementation: 'Внедрение',
  motivation: 'Мотивация'
};

const ToolbarArchimate = ({
  tool,
  setTool,
  zoomIn,
  zoomOut,
  fitToBoard,
  centerToSelection,
  onLoadDiagram,
  onSaveDiagram,
  onOpenDiagramManager,
  _currentDiagramId,
  _zoom,
  elements,
  _currentDiagramMeta,
  topOffset = 0,
  onExportImage,
  onUndo,
  onRedo,
  canUndo = false,
  canRedo = false,
  onBeforeNavigate
}) => {
  const { theme, toggleTheme, currentTheme } = useTheme();
  const navigate = useNavigate();
  const [selectedLayer, setSelectedLayer] = useState('application');
  const [showLayerMenu, setShowLayerMenu] = useState(false);
  const [showElementMenu, setShowElementMenu] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);

  const layerMenuRef = useRef(null);
  const elementMenuRef = useRef(null);
  const exportMenuRef = useRef(null);

  const layerElements = getElementsByLayer(selectedLayer);
  const layerColors = getArchimateColors(selectedLayer);

  const iconStyle = { width: 18, height: 18 };
  const guardedNavigate = (target) => {
    if (typeof onBeforeNavigate === 'function' && !onBeforeNavigate()) return;
    navigate(target);
  };

  // Закрытие меню при клике вне
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (layerMenuRef.current && !layerMenuRef.current.contains(e.target)) setShowLayerMenu(false);
      if (elementMenuRef.current && !elementMenuRef.current.contains(e.target)) setShowElementMenu(false);
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) setShowExportMenu(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectElement = (elementKey) => {
    setTool(`archimate-${elementKey}`);
    setShowElementMenu(false);
  };

  return (
    <div style={{
      position: 'absolute',
      top: topOffset,
      left: 0,
      right: 0,
      height: 56,
      zIndex: 2000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: theme.toolbar.background,
      borderBottomWidth: '1px',
      borderBottomStyle: 'solid',
      borderBottomColor: theme.toolbar.border,
      padding: '0 16px',
      boxShadow: theme.toolbar.shadow
    }}>
      {/* Левая секция */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <ToolbarButton title="Назад" onClick={() => guardedNavigate(-1)}>
          <ArrowBackIcon style={iconStyle} />
        </ToolbarButton>

        <div style={{ fontSize: 18, fontWeight: 'bold', color: theme.toolbar.text, marginRight: 16 }}>
          ArchiMate
        </div>

        <ToolbarButton title="Новая диаграмма">
          <DescriptionIcon style={iconStyle} />
        </ToolbarButton>

        <ToolbarButton title="Сохранить диаграмму (Ctrl+S)" onClick={onSaveDiagram || onLoadDiagram}>
          <SaveIcon style={iconStyle} />
        </ToolbarButton>

        {onOpenDiagramManager && (
          <ToolbarButton title="Управление диаграммами" onClick={onOpenDiagramManager}>
            <DescriptionIcon style={iconStyle} />
          </ToolbarButton>
        )}

        {onExportImage && (
          <div style={{ position: 'relative' }} ref={exportMenuRef}>
            <ToolbarButton title="Экспорт" onClick={() => setShowExportMenu(!showExportMenu)} active={showExportMenu}>
              <ImageIcon style={iconStyle} />
            </ToolbarButton>
            {showExportMenu && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, marginTop: 4,
                background: theme.toolbar.background, border: `1px solid ${theme.toolbar.border}`,
                borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 3000, minWidth: 100
              }}>
                {['png', 'jpg', 'svg'].map(fmt => (
                  <button key={fmt} onClick={() => { onExportImage(fmt); setShowExportMenu(false); }}
                    style={{ display: 'block', width: '100%', padding: '8px 16px', backgroundColor: 'transparent',
                      border: 'none', color: theme.toolbar.buttonText, textAlign: 'left', cursor: 'pointer',
                      textTransform: 'uppercase' }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = theme.name === 'light' ? '#f1f5f9' : '#374151'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  >{fmt}</button>
                ))}
              </div>
            )}
          </div>
        )}

        <div style={{ width: 1, height: 24, backgroundColor: theme.toolbar.border, margin: '0 8px' }} />

        {/* Undo/Redo */}
        <ToolbarButton
          title="Назад (Ctrl+Z)"
          onClick={() => canUndo && onUndo?.()}
          active={false}
          style={{ opacity: canUndo ? 1 : 0.5, cursor: canUndo ? 'pointer' : 'not-allowed' }}
        >
          <UndoIcon style={iconStyle} />
        </ToolbarButton>

        <ToolbarButton
          title="Вперёд (Ctrl+Shift+Z)"
          onClick={() => canRedo && onRedo?.()}
          active={false}
          style={{ opacity: canRedo ? 1 : 0.5, cursor: canRedo ? 'pointer' : 'not-allowed' }}
        >
          <RedoIcon style={iconStyle} />
        </ToolbarButton>

        <div style={{ width: 1, height: 24, backgroundColor: theme.toolbar.border, margin: '0 8px' }} />

        {/* Выбор инструмента */}
        <ToolbarButton title="Выбор" onClick={() => setTool('select')} active={tool === 'select'}>
          <MouseIcon style={iconStyle} />
        </ToolbarButton>

        <ToolbarButton title="Связь" onClick={() => setTool('archimate-arrow')} active={tool === 'archimate-arrow'}>
          <ArrowForwardIcon style={iconStyle} />
        </ToolbarButton>

        <div style={{ width: 1, height: 24, backgroundColor: theme.toolbar.border, margin: '0 8px' }} />

        {/* Выбор слоя */}
        <div style={{ position: 'relative' }} ref={layerMenuRef}>
          <ToolbarButton
            title="Выбрать слой"
            onClick={() => setShowLayerMenu(!showLayerMenu)}
            active={showLayerMenu}
            style={{ minWidth: 140, backgroundColor: layerColors.fill, borderColor: layerColors.stroke }}
          >
            <span>{LAYER_LABELS[selectedLayer] || selectedLayer}</span>
            <ExpandMoreIcon style={{ width: 16, height: 16 }} />
          </ToolbarButton>
          {showLayerMenu && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, marginTop: 4,
              background: theme.toolbar.background, border: `1px solid ${theme.toolbar.border}`,
              borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 3000, minWidth: 160
            }}>
              {Object.entries(ARCHIMATE_LAYERS).map(([key, value]) => {
                const lColors = getArchimateColors(value);
                return (
                  <button key={key} onClick={() => { setSelectedLayer(value); setShowLayerMenu(false); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 12px',
                      backgroundColor: selectedLayer === value ? lColors.fill : 'transparent',
                      border: 'none', color: theme.toolbar.buttonText, textAlign: 'left', cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => e.target.style.background = lColors.fill}
                    onMouseLeave={(e) => e.target.style.background = selectedLayer === value ? lColors.fill : 'transparent'}
                  >
                    <span style={{ width: 12, height: 12, borderRadius: 2, background: lColors.fill, border: `1px solid ${lColors.stroke}` }} />
                    {LAYER_LABELS[value] || value}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Выбор элемента */}
        <div style={{ position: 'relative' }} ref={elementMenuRef}>
          <ToolbarButton
            title="Выбрать элемент"
            onClick={() => setShowElementMenu(!showElementMenu)}
            active={showElementMenu}
            style={{ minWidth: 180 }}
          >
            <span>Элементы ({layerElements.length})</span>
            <ExpandMoreIcon style={{ width: 16, height: 16 }} />
          </ToolbarButton>
          {showElementMenu && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, marginTop: 4,
              background: theme.toolbar.background, border: `1px solid ${theme.toolbar.border}`,
              borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 3000, minWidth: 220,
              maxHeight: 400, overflowY: 'auto'
            }}>
              {layerElements.map((el) => {
                const isActive = tool === `archimate-${el.key}`;
                const iconPath = getArchimateIconPath(el.type);
                return (
                  <button key={el.key} onClick={() => handleSelectElement(el.key)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 12px',
                      backgroundColor: isActive ? layerColors.fill : 'transparent',
                      border: 'none', color: theme.toolbar.buttonText, textAlign: 'left', cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => e.target.style.background = layerColors.fill}
                    onMouseLeave={(e) => e.target.style.background = isActive ? layerColors.fill : 'transparent'}
                  >
                    {iconPath && <img src={iconPath} alt="" style={{ width: 18, height: 18 }} />}
                    {el.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Центральная секция - Навигация */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <ToolbarButton title="Увеличить" onClick={zoomIn}>
          <ZoomInIcon style={iconStyle} />
        </ToolbarButton>
        <ToolbarButton title="Уменьшить" onClick={zoomOut}>
          <ZoomOutIcon style={iconStyle} />
        </ToolbarButton>
        <ToolbarButton title="Вместить все" onClick={fitToBoard}>
          <CenterFocusStrongIcon style={iconStyle} />
        </ToolbarButton>
        <ToolbarButton title="К выделенному" onClick={centerToSelection}>
          <GpsFixedIcon style={iconStyle} />
        </ToolbarButton>
      </div>

      {/* Правая секция */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <ToolbarButton title={`Тема: ${currentTheme}`} onClick={toggleTheme}>
          {currentTheme === 'dark' ? <LightModeIcon style={iconStyle} /> : <DarkModeIcon style={iconStyle} />}
        </ToolbarButton>

        {elements.length > 0 && (
          <div style={{ color: theme.toolbar.text, fontSize: 12, opacity: 0.7, marginLeft: 8 }}>
            {elements.length} элементов • ArchiMate
          </div>
        )}
      </div>
    </div>
  );
};

export default ToolbarArchimate;
